import type { DisplayObject, BaseReel} from "@gamzix/slot_core";

import {CONST} from '../../../src/const';

class GameReelsetComponent extends SlotGame.src.component.ReelsetComponent {
    public TIME_STOP_DELAY = 400;
    public MIN_SPIN_DURATION = 300;
    protected notUsableSymbols = [0,1,2,11,12];
    protected usableSymbols = [3,4,5,6,7,8,9,10];

    //@override
    static events = {
        ...SlotGame.src.component.ReelsetComponent.events,
        SYMBOL_START_REPLACE: "GameReelsetComponent.SYMBOL_START_REPLACE"
    }

    async onStartBonusGame() {
        await game.utils.delay(500);

        super.onStartBonusGame();
    }

    async onFinishBonusGame() {
        await game.utils.delay(500);

        super.onFinishBonusGame();
    }

    // correct mask for bonus game
    createCellReelSetMasks(symbolHeight) {
        for (let i = 0; i < this.rows; i++) {
            const mask: DisplayObject = game.builder.create({
                "type": "FastRect",
                "name": `cell_reelset_mask.row_${i}`,
                "width": this.rowWidth,
                "height": symbolHeight - 2.5,
                "position": {"x": 0, "y": this.getRowPosition(i, symbolHeight) - 4.5}
            })
            this.view.addChild(mask);
        }
    }

    checkShowWinShowWinLines() {
        const isOneLine = this.store.gameData.lines.length == 1 && this.store.gameData.positions.length === 0;
        if (this.store.gameData.lines.length >= this.options.config.linesToSkip || (isOneLine && !this.checkShowBigWin())) return false;

        return this.store.gameData.hasLineWins() &&
            !(
                this.store.gameData.freeSpinsGame.isPlaying() ||
                this.store.gameData.freeSpinsGame.hasSpins() ||
                this.store.gameData.bonusGame.isPlaying() ||
                this.store.gameData.bonusGame.hasSpins() ||
                this.store.autoplay.isActive
            );
    }

    getAnticipationReelsMap(): boolean[] {
        const {gameData} = this.store;
        const map = [false, false, false, false, false];

        if (this.store.gameData.bonusGame.isPlaying()) {
            return map;
        }

        const getReplacedValue = (valueNormal, valueFreeSpin) => {
            return valueFreeSpin !== undefined ? valueFreeSpin : valueNormal;
        }

        this.anticipationConfig.triggers.forEach(({
            triggerSymbolList,
            maxCountScatterOnReel,
            maxCountScatterOnReelFreeSpinGame,
            minCountScatterForStart,
            minCountScatterForStartFreeSpinGame,
            countScattersForStart,
            maxCountScattersForStart,
            filter,
            filterFreeSpinGame

        }) => {
            const isFreeSpinGame = this.store.gameData.freeSpinsGame.isPlaying();
            if (isFreeSpinGame) {
                filter = getReplacedValue(filter, filterFreeSpinGame);
                minCountScatterForStart = getReplacedValue(minCountScatterForStart, minCountScatterForStartFreeSpinGame);
                maxCountScatterOnReel = getReplacedValue(maxCountScatterOnReel, maxCountScatterOnReelFreeSpinGame);
            }

            let countScatters = 0;
            const pFilter = filter || gameData.reels.map(() => true);

            gameData.reels
                .forEach((reelData, index) => {
                    if(maxCountScattersForStart && maxCountScattersForStart <= countScatters) {
                        map[index] = false;
                        return;
                    }

                    const countOnReel = reelData.filter(symbolData => triggerSymbolList.includes(symbolData?.symbol.toString())).length;
                    const maxCountPossibleScatters = countScatters + (this.column - index) * maxCountScatterOnReel;

                    if (countScatters >= minCountScatterForStart && maxCountPossibleScatters >= countScattersForStart && Boolean(pFilter[index])) {
                        map[index] = true;
                    }

                    countScatters += countOnReel;
                });
        });

        return map;
    }

    async onIdleWaitSpin() {
        this.playAllIdleAnimation();

        if (this.store.gameData.mode != "bonus") {
            await this.waitClick();
        }
    }

    async showBonusSymbolsWin() {

        this.playAllLoseAnimation();

        let positions = [];

        this.store.gameData.reels.forEach((reel, x) => {
            reel.forEach((sym, y) => {
                if (sym?.symbol == CONST.SYMBOLS.COIN || sym?.symbol == CONST.SYMBOLS.COLLECTOR) {
                    positions.push([x, y]);
                }
            })
        })

        // for winFrame
        game.emit("ReelsetComponent.SHOW_BONUS_SYMBOLS_WIN", {positions})
        
        const isSpinHaveHeapFeatureTriggerBonus = game.store.gameData.replace.length

        if (positions.length >= 6 || isSpinHaveHeapFeatureTriggerBonus) {
            await this.showWins(positions);
        }
    }

    async showScattersSymbolsWin() {
        const minScatterForWin: number = this.store.gameData.mode !== "freespins" ? 3 : 2;
        this.playAllLoseAnimation();

        let positions = [];

        this.store.gameData.reels.forEach((reel, x) => {
            reel.forEach((sym, y) => {
                if (sym?.symbol == CONST.SYMBOLS.SCATTER) {
                    positions.push([x, y]);
                }
            })
        })

        if (positions.length >= minScatterForWin) {
            game.emit("ReelsetComponent.SHOW_WIN_SCATTER_LINE")

            await this.showWins(positions);
        }
        this.playAllLoseAnimation();
    }

    //need to get access to this method for changing getNextSymbolId() for fixing SAST-144, impossible to use supper()
    //TODO need to change inside a framework for upcoming games
    async stopRotate(): Promise<void> {
        if (this.state !== this.STATES.SPINNING) return;

        this.skip = null;

        let nextReel = this.nextSpinningReel;

        const { gameData, gameSettings } = this.store;

        let isFastStop = gameSettings.isTurboSpin || this.isSkipped;

        if (isFastStop) {
            this.reels.forEach(reel => reel.fastStop());
        }

        while (nextReel) {
            // start anticipation for first reel
            if (!isFastStop && nextReel.index == 0 && this.getAnticipationReelsMap()[0]) {
                await Promise.race([
                    this.startAnticipationOnReel(nextReel),
                    game.oncePromise(UIComponent.events.SKIP_ANIMATIONS).then(() => (isFastStop = true))
                ]);
            }

            const strip = gameData.reels[nextReel.index].map(reelData =>
                reelData?.hasOwnProperty('symbol') ? reelData?.symbol : this.getBonusGameEmptySymbolId()
            );

            const extraSymbolsOnTop = [];
            const extraSymbolsOnBottom = [];

            for (let i = 0; i < nextReel.countExtraSymbols; i++) {
                //caring boolean inside for showing that overlapping symbols need to change for other symbols
                //@ts-ignore
                extraSymbolsOnTop.push(nextReel.strip.getNextSymbolId(true));
                //@ts-ignore
                extraSymbolsOnBottom.push(nextReel.strip.getNextSymbolId(true));
            }

            nextReel.stopRotate([...extraSymbolsOnTop, ...strip, ...extraSymbolsOnBottom]);

            if (!isFastStop) {
                await Promise.race([
                    //@ts-ignore
                    game.oncePromise(BaseReel.events.REEL_WILL_STOP),
                    this.waitClick().then(() => (isFastStop = true)),
                    game.oncePromise(UIComponent.events.SKIP_ANIMATIONS).then(() => (isFastStop = true))
                ]);
            }

            if (nextReel.isAnticipation) {
                game.emit(ReelsetComponent.events.REEL_ANTICIPATION_FINISHED, { reelId: nextReel.index });
                nextReel.anticipationStop();
            }

            nextReel = this.nextSpinningReel;

            if (!isFastStop && nextReel) {
                await Promise.race([
                    game.utils.delay(this.getTimeStopDelay() / this.store.gameSettings.gameSpeed),
                    this.waitClick().then(() => (isFastStop = true)),
                    game.oncePromise(UIComponent.events.SKIP_ANIMATIONS).then(() => (isFastStop = true))
                ]);
            }

            if (!isFastStop && nextReel && nextReel.index != 0 && this.getAnticipationReelsMap()[nextReel.index]) {
                await Promise.race([
                    this.startAnticipationOnReel(nextReel),
                    game.oncePromise(UIComponent.events.SKIP_ANIMATIONS).then(() => (isFastStop = true))
                ]);
            }
        }

        await this.onLastReelStopped();

        this.isSkipped = false;
        await game.utils.delay(50);
    }

    override async onReplaceSymbols() {
        const isHaveCustomReplaceSymbols = !!game.component.heapFeature;
        if (isHaveCustomReplaceSymbols) return
        
        super.onReplaceSymbols
    }

    getFSMHandlers(): any {
        return {
            ...super.getFSMHandlers(),
            CHECK: {
                ...super.getFSMHandlers()['CHECK'],
                showBonusWin: {
                    on: this.showBonusSymbolsWin.bind(this),
                    check: () => this.store.gameData.mode === "bonus" && (this.store.gameData.prevMode === "main" || this.store.gameData.prevMode === "freespins")
                },
                showScattersWin: {
                    on: this.showScattersSymbolsWin.bind(this),
                    check: () => (this.store.gameData.prevMode === "main" || this.store.gameData.prevMode === "freespins")
                }
            }
        }
    }
}

export { GameReelsetComponent }

// @ts-ignore
Game.src.component.ReelsetComponent = GameReelsetComponent